import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { StaffFeesPendingReportService, StudentFeeOverdueRecord } from '../services/staff-fees-pending-report.service';
import { StaffFeesReportStudentHistoryNavState } from '../fees-report-student-history/staff-fees-report-student-history.component';
import { StaffFeesReportNavState } from '../fees-report/staff-fees-report.component';
import { StaffSessionService } from '../services/staff-session.service';

export interface StaffFeesReportStudentsNavState {
  classNumber: string;
  classDivision: string;
}

@Component({
  selector: 'app-staff-fees-report-students',
  templateUrl: './staff-fees-report-students.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-fees-report-students.component.css']
})
export class StaffFeesReportStudentsComponent implements OnInit {
  staffId: number | string | null = null;
  currencySymbol = '';

  classId: string = '';
  className = '';
  classNumber = '';
  classDivision = '';

  activeTab: 'active' | 'deactivated' = 'active';
  fetchingStudentListData = true;

  activatedStudentData: StudentFeeOverdueRecord[] = [];
  deactivatedStudentData: StudentFeeOverdueRecord[] = [];
  totalActiveStudentCount = 0;
  totalDeactivateStudentCount = 0;
  totalActiveStudentPendingAmount = 0;
  totalPendingAmount = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private staffFeesPendingReportService: StaffFeesPendingReportService,
    private staffSession: StaffSessionService
  ) {}

  ngOnInit(): void {
    const staffDetails = this.staffSession.getStaffDetails();
    const info = this.staffSession.getSchoolAndStaffInfo();
    this.staffId = staffDetails?.staffId ?? info?.StaffId ?? null;
    this.currencySymbol = info?.currencySymbol || staffDetails?.currencyType || '';

    this.classId = this.route.snapshot.paramMap.get('classId') || '';
    // Router.navigate's { state } extras land in history.state by the time
    // this component activates — getCurrentNavigation() is only valid
    // during the navigation itself, which has already finished here.
    const state = history.state as StaffFeesReportStudentsNavState;
    this.classNumber = state?.classNumber || '';
    this.classDivision = state?.classDivision || '';
    this.className = this.classNumber ? `${this.classNumber}-${this.classDivision}` : '';

    this.loadStudentList();
  }

  private async loadStudentList() {
    if (!this.staffId || !this.classId) { this.fetchingStudentListData = false; return; }
    this.fetchingStudentListData = true;
    try {
      const response = await firstValueFrom(this.staffFeesPendingReportService.getOverdueStudentList(this.staffId, this.classId));
      if (response?.status && response.body) {
        const students = response.body.studentFeesOverdueData || [];
        // Split client-side from the one response — no second API call, per
        // the mobile app's behaviour.
        this.activatedStudentData = students.filter(s => s.isBlocked == 0);
        this.deactivatedStudentData = students.filter(s => s.isBlocked == 1);
        this.totalActiveStudentCount = response.body.totalActiveStudentPendingCount || 0;
        this.totalDeactivateStudentCount = response.body.totalDeactiveStudentPendingCount || 0;
        this.totalActiveStudentPendingAmount = response.body.totalActiveStudentPendingAmount || 0;
        this.totalPendingAmount = response.body.totalPendingAmount || 0;
      } else {
        this.activatedStudentData = [];
        this.deactivatedStudentData = [];
      }
    } catch {
      // No error toast — matches the mobile app, which falls through
      // silently to the empty-state UI on failure.
      this.activatedStudentData = [];
      this.deactivatedStudentData = [];
    } finally {
      this.fetchingStudentListData = false;
    }
  }

  setTab(tab: 'active' | 'deactivated') {
    if (this.activeTab === tab) { return; }
    this.activeTab = tab;
  }

  get students(): StudentFeeOverdueRecord[] {
    return this.activeTab === 'active' ? this.activatedStudentData : this.deactivatedStudentData;
  }

  // Active tab shows the "excluding deactivated" total; deactivated tab
  // shows the "including deactivated" grand total.
  get headerTotal(): number {
    return this.activeTab === 'active' ? this.totalActiveStudentPendingAmount : this.totalPendingAmount;
  }

  get headerTotalLabel(): string {
    return this.activeTab === 'active' ? 'totalExcludingSum' : 'totalIncludingSum';
  }

  formatAmount(value: number): string {
    return `${this.currencySymbol}${(value || 0).toLocaleString('en-IN')}`;
  }

  pendingAmount(s: StudentFeeOverdueRecord): number {
    return s.feeDetails?.PendingAmount ?? 0;
  }

  // Lightweight stand-in for the mobile app's info bottom sheet — a plain
  // explanatory popover with no actions, toggled by tapping the header's
  // info icon.
  showInfoPopover = false;

  toggleInfoPopover() {
    this.showInfoPopover = !this.showInfoPopover;
  }

  get infoMessageKey(): string {
    return this.activeTab === 'active' ? 'totalCollectionFromActive' : 'totalCollectionFromDeactivated';
  }

  goBack() {
    // This screen only ever exists under the Pending flow — explicitly
    // tell fees-report which tab to restore, since navigating "back" here
    // is itself a forward router.navigate and would otherwise remount
    // fees-report defaulting to its 'overdue' tab.
    const state: StaffFeesReportNavState = { reportType: 'pending' };
    this.router.navigate(['/staff/fees-report'], { state });
  }

  openStudent(s: StudentFeeOverdueRecord) {
    const state: StaffFeesReportStudentHistoryNavState = {
      studentName: s.studentName,
      className: this.className,
      classNumber: this.classNumber,
      classDivision: this.classDivision
    };
    this.router.navigate(['/staff/fees-report/class', this.classId, 'student', s.studentId], { state });
  }
}
